import type { BranchTheme } from '@branchjs/neo-components';
import { Skeleton, withStyles } from '@branchjs/neo-components';
import type { ClassNameMap } from '@mui/styles';
import classNames from 'classnames';
import NoResults from 'client/js/main/components/NoResults';
import type { RootState } from 'client/js/redux';
import * as R from 'ramda';
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import {
  COST_DATA_WHITELISTED_AD_PARTNERS,
  GOOGLE_ANALYTICS_V4_FIREBASE_MACHINE_NAME,
  GOOGLE_ANALYTICS_V4_WEB_MACHINE_NAME,
  HUAWEI_MACHINE_NAME,
  TIKTOK_SAN_MACHINE_NAME,
} from '../../../../../shared/constants/adPartnerConstants';
import {
  ENABLE_HUAWEI_AD_PARTNER,
  ENABLE_TIKTOK_SAN,
  GOOGLE_ANALYTICS_V4,
} from '../../../../../shared/constants/flipperFlags';
import type { CustomPartnerLimitResponse } from '../../../../../shared/types/customPartnerLimits.types';
import type { AdPartner } from '../../../main/types/adPartnerTypes';
import { isAdPartnerVisible, selectCustomPartnerLimits } from '../../../redux/domains/AdPartner/AdPartner.selectors';
import { isFeatureEnabled } from '../../../redux/domains/FlipperUpdates/Flipper.selectors';
import PartnerCard from './PartnerCard';

const styles = (theme: BranchTheme) =>
  ({
    emptyListWrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',

      '& .MuiTypography-h3': {
        ...theme.typography.h5,
      },
      '& .MuiTypography-body1': {
        ...theme.typography.body3,
      },
    },
    emptyListImage: {
      marginBottom: theme.spacing(2),
    },
    emptyListText: {
      marginBottom: theme.spacing(1),
      textAlign: 'center',
    },
  }) as any;

export type Props = {
  classes: ClassNameMap<'emptyListWrapper' | 'emptyListImage' | 'emptyListText'>;
  scrollTop?: boolean;
  showAllPartners?: boolean;
  data: Array<AdPartner>;
  enabledText?: string;
  disabledText?: string;
  selectedPartner?: AdPartner | null;
  accessResources?: Array<string>;
  permission?: number;
  onClick: (partner: AdPartner) => void;
  onDisableClick?: (partner: AdPartner) => void;
  loading?: boolean | null;
  tikTokSANEnabled: boolean;
  huaweiEnabled: boolean;
  googleAnalyticsV4Enabled: boolean;
  customPartnerLimits: CustomPartnerLimitResponse | null;
  filter?: string;
};

export class PartnerList extends PureComponent<Props> {
  static defaultProps = {
    data: [],
    scrollTop: false,
    showAllPartners: false,
    enabledText: 'ENABLED PARTNERS',
    disabledText: 'DISABLED PARTNERS',
  };

  componentDidUpdate() {
    if (this.props.scrollTop) {
      // @ts-ignore
      ReactDOM.findDOMNode(this).scrollTop = 0;
    }
  }

  isAdPartnerAvailable = (adPartnerMachineName: string): boolean => {
    const { tikTokSANEnabled, huaweiEnabled, googleAnalyticsV4Enabled } = this.props;

    switch (adPartnerMachineName) {
      case TIKTOK_SAN_MACHINE_NAME:
        return tikTokSANEnabled;

      case HUAWEI_MACHINE_NAME:
        return huaweiEnabled;

      case GOOGLE_ANALYTICS_V4_WEB_MACHINE_NAME:
      case GOOGLE_ANALYTICS_V4_FIREBASE_MACHINE_NAME:
        return googleAnalyticsV4Enabled;

      default:
        return true;
    }
  };

  getPartnersToDisplay = (partners: AdPartner[], enabled: boolean) => {
    const activePartners = partners.filter((partner) => {
      return this.props.showAllPartners || isAdPartnerVisible(partner);
    });
    return activePartners.filter((partner: AdPartner) => {
      const ingestionEnabled = R.path(['ingestion', 'enabled'], partner);

      if (ingestionEnabled) {
        return ingestionEnabled === enabled;
      }

      return !!partner.enabled === enabled && this.isAdPartnerAvailable(partner.machine_name);
    });
  };

  renderCards = (partners: AdPartner[]) => {
    const filteredPartners = !this.props.filter
      ? partners
      : partners.filter((partner) => {
          if (!this.props.filter) {
            return true;
          }

          switch (this.props.filter) {
            case 'cost_data':
              const hasCostIngestionParams = !!partner.cost_ingestion_parameters;
              const isCostIngestionWhitelisted = COST_DATA_WHITELISTED_AD_PARTNERS.includes(partner.machine_name);
              return hasCostIngestionParams || isCostIngestionWhitelisted;
            default:
              return true;
          }
        });

    return filteredPartners.map((partner: AdPartner, i: number) => {
      const isCustom = this.props.customPartnerLimits?.customPartners?.some(
        (customPartner) => customPartner.machineName === partner.machine_name
      );
      const isOrgLevel = this.props.customPartnerLimits?.customPartners?.some(
        (customPartner) => customPartner.machineName === partner.machine_name && customPartner.isOrgLevel
      );

      return (
        <PartnerCard
          accessResources={this.props.accessResources}
          permission={this.props.permission}
          onClick={this.props.onClick.bind(null, partner)}
          partner={partner}
          selectedPartner={this.props.selectedPartner}
          key={partner.machine_name}
          index={i}
          isCustom={isCustom}
          isOrgLevel={isOrgLevel}
        />
      );
    });
  };

  renderLoadingCards = () => {
    return (
      <>
        <Skeleton className="partner-list__loading-text-placeholder" variant="text" height={16} />
        {new Array(11).fill(0).map((_, index) => {
          return (
            <Skeleton
              className={classNames('default-skeleton', 'partner-list__loading-placeholder')}
              key={index}
              variant="rectangular"
              height={50}
            />
          );
        })}
      </>
    );
  };

  renderPartners = () => {
    const { data, classes, enabledText, disabledText } = this.props;
    const enabledPartners = this.getPartnersToDisplay(data, true);
    const disabledPartners = this.getPartnersToDisplay(data, false);
    const allPartners = [...enabledPartners, ...disabledPartners];

    if (!allPartners.length) {
      return (
        <div className={classes.emptyListWrapper}>
          <NoResults />
        </div>
      );
    }

    return (
      <>
        {!!enabledPartners.length && (
          <>
            <div className="partner-list__header">
              <span className="partner-list__header-text">{enabledText}</span>
            </div>
            <div className="h_mb-40" data-testid="enabled-cards">
              {this.renderCards(enabledPartners) || <div className="h_p-20" key={1} />}
            </div>
          </>
        )}
        {!!disabledPartners.length && (
          <>
            <div className="partner-list__header">
              <span className="partner-list__header-text">{disabledText}</span>
            </div>
            <div data-testid="disabled-cards">{this.renderCards(disabledPartners)}</div>
          </>
        )}
      </>
    );
  };

  render() {
    const { loading } = this.props;

    return (
      <>
        <div className="partner-list">{loading ? this.renderLoadingCards() : this.renderPartners()}</div>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  tikTokSANEnabled: isFeatureEnabled(state, ENABLE_TIKTOK_SAN),
  huaweiEnabled: isFeatureEnabled(state, ENABLE_HUAWEI_AD_PARTNER),
  googleAnalyticsV4Enabled: isFeatureEnabled(state, GOOGLE_ANALYTICS_V4),
  customPartnerLimits: selectCustomPartnerLimits(state),
});

const ConnectedPartnerList = withStyles(styles)(connect(mapStateToProps, null)(PartnerList));
export default ConnectedPartnerList;
